import { useMemo } from "react";
import type { LucideIcon } from "lucide-react";

export type IconEntry = {
  name: string;
  Component: LucideIcon;
};

type IconPickerProps = {
  icons: IconEntry[];
  search: string;
  selected: string;
  onSearchChange: (value: string) => void;
  onSelect: (name: string) => void;
  noResultsLabel: string;
  searchPlaceholder: string;
  title: string;
};

const formatIconName = (name: string) =>
  name
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/([A-Z])([A-Z][a-z])/g, "$1 $2")
    .trim();

export function IconPicker({
  icons,
  search,
  selected,
  onSearchChange,
  onSelect,
  noResultsLabel,
  searchPlaceholder,
  title,
}: IconPickerProps) {
  const filteredIcons = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return icons;
    return icons.filter(({ name }) => name.toLowerCase().includes(query));
  }, [icons, search]);

  return (
    <section className="flc-panel flc-picker">
      <div className="flc-panel-header">
        <h2>{title}</h2>
        <span className="flc-count">{filteredIcons.length}</span>
      </div>
      <input
        type="search"
        className="flc-input"
        placeholder={searchPlaceholder}
        value={search}
        onChange={(event) => onSearchChange(event.target.value)}
        aria-label={searchPlaceholder}
      />
      <div className="flc-icon-grid" role="listbox">
        {filteredIcons.length === 0 && (
          <div className="flc-empty">{noResultsLabel}</div>
        )}
        {filteredIcons.map(({ name, Component }) => (
          <button
            key={name}
            type="button"
            className={`flc-icon-btn ${selected === name ? "selected" : ""}`}
            onClick={() => onSelect(name)}
            title={formatIconName(name)}
            aria-pressed={selected === name}
          >
            <Component size={20} />
          </button>
        ))}
      </div>
      <div className="flc-selection">{formatIconName(selected)}</div>
    </section>
  );
}
